import { Progress as BaseProgress } from '@base-ui/react/progress';
import { type VariantProps, cva } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '../lib/cn';

/**
 * D1 §3 "오렌지의 자리": 진행률은 "지금 흐르고 있는 것"이므로 오렌지가 맞다.
 * 프로토타입도 과제 진행률 바에 오렌지를 썼다(`.fdm-pf-progress .fill`).
 *
 * [D4-19] accent-500이었다. "마크이므로 대비 규칙이 다르다"고 적어두었지만 규칙이
 * 다른 것이지 없는 것이 아니다 — 채움과 트랙의 경계가 곧 정보라 비텍스트 3:1이
 * 적용되는데 2.08:1이었다. `bg-flow`는 이제 accent-700이고 트랙 대비 4.03:1이다.
 */
/*
 * [D4-07] `transition-[width]`였다. 레이아웃 속성을 애니메이션하면 매 프레임
 * 레이아웃이 다시 계산된다. 대신 폭을 100%로 두고 `scaleX`로 줄인다 —
 * 합성 단계에서만 처리되므로 레이아웃을 건드리지 않는다.
 *
 * 채움의 모서리는 트랙이 잘라준다(트랙이 `rounded-pill overflow-hidden`).
 * 그래서 채움 자체에는 반경을 주지 않는다 — scaleX로 눌린 둥근 끝이
 * 타원으로 일그러지는 것도 함께 막힌다. 오른쪽 끝이 직각으로 서는 편이
 * 진행률의 현재 위치를 더 정확하게 가리키기도 한다.
 */
const indicatorVariants = cva(
  'ease-flow duration-slow h-full origin-left motion-safe:transition-transform',
  {
    variants: {
      tone: {
        /** 기본. 진행 중인 것 — 과제 진행률, 업로드, 분석. */
        flow: 'bg-flow',
        /** 축적된 것의 양 — 프로필 완성도처럼 "흐름"이 아닌 지표. */
        brand: 'bg-action',
        /** 완료를 강조할 때. */
        good: 'bg-good',
      },
    },
    defaultVariants: { tone: 'flow' },
  },
);

export interface ProgressProps
  extends ComponentProps<typeof BaseProgress.Root>, VariantProps<typeof indicatorVariants> {
  label?: string;
  showValue?: boolean;
}

/** 온보딩 업로드·분석 진행률 등. `value={null}`이면 indeterminate. */
export function Progress({
  className,
  label,
  showValue,
  tone,
  value,
  min = 0,
  max = 100,
  ...props
}: ProgressProps) {
  /* indeterminate(value가 null·미정)이면 Base UI의 기본 조판에 맡긴다. */
  const ratio =
    typeof value === 'number' && Number.isFinite(value) && max > min
      ? Math.min(1, Math.max(0, (value - min) / (max - min)))
      : null;

  return (
    <BaseProgress.Root
      className={cn('gap-tight flex w-full flex-col', className)}
      value={value}
      min={min}
      max={max}
      {...props}
    >
      {label || showValue ? (
        <div className="text-ink-muted flex items-baseline justify-between text-xs">
          {label ? <BaseProgress.Label>{label}</BaseProgress.Label> : <span />}
          {showValue ? <BaseProgress.Value className="font-mono font-medium" /> : null}
        </div>
      ) : null}

      <BaseProgress.Track className="rounded-pill bg-track h-1.5 w-full overflow-hidden">
        <BaseProgress.Indicator
          className={indicatorVariants({ tone })}
          /* Base UI가 인라인으로 넣는 `width: N%`를 덮어쓴다. */
          style={ratio === null ? undefined : { width: '100%', transform: `scaleX(${ratio})` }}
        />
      </BaseProgress.Track>
    </BaseProgress.Root>
  );
}
